import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Grid,
  Divider
} from '@mui/material';
import {
  CreditCard,
  CheckCircle,
  Warning,
  Security
} from '@mui/icons-material';

interface CustomerPaymentInfoProps {
  paymentMethodDetails?: {
    hasActivePaymentMethod: boolean;
    cardCount: number;
    defaultCard?: {
      cardLast4?: string;
      cardBrand?: string;
      cardExpMonth?: number;
      cardExpYear?: number;
    };
    lastUsedAt?: Date;
    verificationStatus?: 'verified' | 'pending' | 'failed';
    verificationDate?: string;
    verificationAmount?: string;
    cardLast4?: string;
    cardBrand?: string;
  };
}

const CustomerPaymentInfo: React.FC<CustomerPaymentInfoProps> = ({
  paymentMethodDetails
}) => {
  if (!paymentMethodDetails?.hasActivePaymentMethod) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <CreditCard color="disabled" />
            <Typography variant="h6">Payment Information</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Warning color="warning" />
          <Typography color="text.secondary">
            Payment method not yet set up through Stripe
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          You'll be redirected to Stripe's secure payment page when you accept a bid or service.
          Stripe handles all payment processing securely.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          <strong>Need help?</strong> Contact Stripe Support: 
          <a href="https://support.stripe.com/" target="_blank" rel="noopener noreferrer" style={{ marginLeft: '5px' }}>
            https://support.stripe.com/
          </a>
        </Typography>
        </CardContent>
      </Card>
    );
  }

  const getCardBrandIcon = (brand?: string) => {
    if (!brand) return <CreditCard />;
    
    const brandLower = brand.toLowerCase();
    if (brandLower.includes('visa')) return '💳';
    if (brandLower.includes('mastercard') || brandLower.includes('master')) return '💳';
    if (brandLower.includes('amex') || brandLower.includes('american')) return '💳';
    if (brandLower.includes('discover')) return '💳';
    return '💳';
  };

  const getVerificationStatus = () => {
    const status = paymentMethodDetails.verificationStatus || 'pending';
    switch (status) {
      case 'verified':
        return {
          color: 'success' as const,
          icon: <CheckCircle />,
          label: 'Verified',
          description: 'Card has been verified and is ready for payments'
        };
      case 'failed':
        return {
          color: 'error' as const,
          icon: <Warning />,
          label: 'Verification Failed',
          description: 'Card verification failed. Please update your payment method.'
        };
      default:
        return {
          color: 'warning' as const,
          icon: <Warning />,
          label: 'Pending Verification',
          description: 'Card verification is pending'
        };
    }
  };

  const verificationStatus = getVerificationStatus();
  const cardBrand = paymentMethodDetails.cardBrand || paymentMethodDetails.defaultCard?.cardBrand;
  const cardLast4 = paymentMethodDetails.cardLast4 || paymentMethodDetails.defaultCard?.cardLast4;
  const cardExpMonth = paymentMethodDetails.defaultCard?.cardExpMonth;
  const cardExpYear = paymentMethodDetails.defaultCard?.cardExpYear;

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <CreditCard color="primary" />
          <Typography variant="h6">Payment Information</Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Chip
            icon={verificationStatus.icon}
            label={verificationStatus.label}
            color={verificationStatus.color}
            size="small"
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {verificationStatus.description}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Payment is processed securely through Stripe. Your card information is not stored on our servers.
        </Typography>
        {verificationStatus.color === 'error' && (
          <Typography variant="body2" sx={{ mt: 2 }}>
            <strong>Need help?</strong> Contact Stripe Support: 
            <a href="https://support.stripe.com/" target="_blank" rel="noopener noreferrer" style={{ marginLeft: '5px' }}>
              https://support.stripe.com/
            </a>
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default CustomerPaymentInfo;
